import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './chat-oa.css';
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
// Import JWT service early to ensure it's initialized
import './JWTAuthService';
import ChatBotPage from './ChatBotPage';
import reportWebVitals from './reportWebVitals';

import { Provider, useAtom } from 'jotai';
import { authenticationAtom, jwtTokensAtom } from './State';
import { jwtAuthService } from './JWTAuthService';
import Protected from './Protected';
import { LoginPage } from './website/LoginPage';
import { SignUpPage } from './website/SignUpPage';
import { OAuthBridgePage } from './website/OAuthBridgePage';
import { LogoutPage } from './website/LogoutPage';
import MagicLinkPage from './MagicLinkPage';
import EntraSsoPage from './EntraSsoPage';
import OnboardingPage from './onboarding/OnboardingPage';
import './i18n.ts';
import { watchIconFont } from './utilities/iconFont';
import SettingsPage from './SettingsPage';

import RulebookPage from "./rulebook/RulebookPage";
import GuidelinesPage from "./guidelines/GuidelinesPage";
import GefmaPortalPage from "./guidelines/GefmaPortalPage";
import AdministrationPage from './administration/AdministrationPage';
import MarkupPage from './MarkupPage';
import EntityPage from './EntityPage';
import TestDIN276 from './TestDIN276';
import { CustomerThemeProvider } from './Theme';


import { EditorTest } from './floorplanEditor/EditorTest';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ModalContextProvider } from './components/ModalProvider';
import { DocumentViewerProvider } from './documentViewer/DocumentViewerContext';
import { Toaster } from '@/components/ui/sonner';
import AppShell from './components/AppShell';
import MapAndImageView from './MapAndImageView';
import PortfolioDashboardPage from './portfolio-dashboard/PortfolioDashboardPage';
import HeatmapPage from './heatmap/HeatmapPage';
import NotFoundPage from './NotFoundPage';

// Debounce ResizeObserver callbacks into the next animation frame. This breaks
// the synchronous resize loop that otherwise triggers the harmless-but-noisy
// "ResizeObserver loop completed with undelivered notifications." error (seen
// e.g. when Radix popovers/comboboxes resize inside dialogs).
if (typeof window !== 'undefined' && window.ResizeObserver) {
    const NativeResizeObserver = window.ResizeObserver;
    window.ResizeObserver = class extends NativeResizeObserver {
        constructor(callback: ResizeObserverCallback) {
            super((entries, observer) => {
                window.requestAnimationFrame(() => callback(entries, observer));
            });
        }
    };
}


// Override console.error to catch the specific error
const originalConsoleError = console.error;
console.error = (...args) => {
  const errorMessage = args.join(' ');
  if (errorMessage.includes("Cannot read properties of null (reading 'options')")) {
    console.error('🚨 CAUGHT THE NULL OPTIONS ERROR! 🚨');
    console.error('Error message:', errorMessage);
    console.error('Stack trace:', new Error().stack);
    console.error('Arguments:', args);
  }
  originalConsoleError.apply(console, args);
};

// Global Error Boundary Component
class GlobalErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Global Error Boundary caught an error:', error);
    console.error('Error info:', errorInfo);
    console.error('Error stack:', error.stack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h1>Something went wrong.</h1>
          <p>Error: {this.state.error?.message}</p>
          <button onClick={() => window.location.reload()}>
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

function RootRedirect() {
  const [authentication] = useAtom(authenticationAtom);
  const [jwtTokens] = useAtom(jwtTokensAtom);
  const { useState, useEffect } = React;
  const [target, setTarget] = useState<string | null>(null);

  useEffect(() => {
    const accessToken = jwtAuthService.getAccessToken();
    const hasJWT = accessToken && !jwtAuthService.isTokenExpired(accessToken);
    const hasSession = authentication &&
      typeof authentication === 'object' &&
      Object.keys(authentication).length > 0 &&
      (authentication.customer || authentication.user || authentication.email);
    setTarget(hasJWT || hasSession ? '/chatbot' : '/login');
  }, [authentication, jwtTokens]);

  if (!target) return null;
  return <Navigate to={target} replace />;
}

function PortfolioDashboardRedirect() {
  return <Navigate to={{ pathname: '/cbos/portfolio-dashboard', search: window.location.search }} replace />;
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// Reveal the Material Symbols icons only once their font is loaded (see utilities/iconFont.ts).
watchIconFont();

root.render(
  <React.StrictMode>
    <Provider>
      <DndProvider backend={HTML5Backend}>
        <BrowserRouter>
          <CustomerThemeProvider>
            <ModalContextProvider>
              <DocumentViewerProvider>
              <GlobalErrorBoundary>
                <Routes>
                  <Route path="/">
                    <Route index element={<RootRedirect />} />
                    <Route path="login" element={<LoginPage />} />
                    <Route path="signup" element={<SignUpPage />} />
                    <Route path="auth/magic" element={<MagicLinkPage />} />
                    <Route path="auth/sso" element={<EntraSsoPage />} />
                    <Route path="onboarding/:token" element={<OnboardingPage />} />
                    <Route path="oauth/bridge" element={<OAuthBridgePage />} />
                    <Route path="logout" element={<LogoutPage />} />
                    <Route path="markup/*" element={<MarkupPage></MarkupPage>} />
                    <Route path="din276/*" element={<TestDIN276></TestDIN276>} />
                    <Route path="editor" element={<EditorTest />} />
                    <Route element={<Protected />}>
                      <Route path="map-and-image" element={<MapAndImageView />} />
                      {/* Sidebar-Seiten teilen sich das AppShell-Layout, damit die
                          Sidebar beim Routenwechsel stehen bleibt. Der Chat läuft
                          mit — dort trägt die Sidebar statt der Bereichsliste die
                          Konversationsliste (siehe AppShell/ChatSidebar). */}
                      {/* Versteckt, ohne Menüeintrag und ohne Portal-Oberfläche: Simulation eines gefma-Downloadportals. */}
                      <Route path="gefma-richtlinien" element={<GefmaPortalPage />} />
                      <Route element={<AppShell />}>
                        <Route path="chatbot" element={<ChatBotPage />} />
                        <Route path="entity" element={<EntityPage />} />
                        {/* Dieselbe Seite unter einer Adresse je Entität — für jeden Typ, auch für die,
                            die nur die generische Ansicht haben (entityPath in tri-model). */}
                        <Route path="entities/:entityType/:entityId" element={<EntityPage />} />
                        <Route path="settings" element={<SettingsPage />} />
                        <Route path="administration" element={<AdministrationPage />} />
                        <Route path="rulebook" element={<RulebookPage />} />
                        <Route path="guidelines" element={<GuidelinesPage />} />
                        <Route path="guidelines/:documentId" element={<GuidelinesPage />} />
                        {/* CBOS pages live under /cbos and share the shell (common
                            header + a short CBOS section list in the sidebar). */}
                        <Route path="cbos/portfolio-dashboard" element={<PortfolioDashboardPage />} />
                        <Route path="cbos/heatmap" element={<HeatmapPage />} />
                      </Route>
                      {/* The bare /portfolio-dashboard is kept as a redirect so links and
                          bookmarks handed out before the move still land. */}
                      <Route
                        path="portfolio-dashboard"
                        element={<PortfolioDashboardRedirect />}
                      />
                      <Route path="private/markup/*" element={<MarkupPage></MarkupPage>} />
                      {/* Catch-all. Sits inside <Protected> so an unknown URL behaves like
                          the private route it is almost always a typo of: signed in you get
                          the 404 page, signed out you get sent to /login. Either way, never
                          the blank page an unmatched URL used to render. */}
                      <Route path="*" element={<NotFoundPage />} />
                    </Route>
                  </Route>
                </Routes>
                <Toaster position="bottom-right" />
              </GlobalErrorBoundary>
              </DocumentViewerProvider>
            </ModalContextProvider>
          </CustomerThemeProvider>
        </BrowserRouter>
      </DndProvider>
    </Provider>
  </React.StrictMode>
);

reportWebVitals();
